<template>
  <AuthWrapper>
    <template #header>
      <h1 class="text-2xl" v-html="t('invitation.title')"></h1>
      <p
        v-if="!errorCodeRef && !isLoadingRef"
        class="text-sm text-muted-foreground"
        v-html="t('invitation.subtitle', { company: whiteLabelRef.companyName || '' })"
      ></p>
    </template>
    <template #body>
      <Card data-test-id="invitation-page">
        <CardContent>
          <div
            v-if="isLoadingRef"
            class="flex flex-col items-center gap-5 text-center"
            data-test-id="invitation-loading"
          >
            <p class="text-sm text-muted-foreground">{{ t('invitation.loading') }}</p>
          </div>

          <div
            v-else-if="errorCodeRef === 'invalid'"
            class="flex flex-col items-center gap-5 text-center"
            data-test-id="invitation-error-invalid"
          >
            <p class="text-sm text-muted-foreground">{{ t('invitation.error.invalid') }}</p>
            <router-link
              :to="ROUTES.INDEX"
              data-test-id="invitation-error-back-to-login"
              class="inline-flex items-center gap-1.5 text-sm font-medium !text-primary !no-underline hover:!underline"
            >
              <ArrowLeftIcon class="size-4" />
              {{ t('invitation.errorBackToLogin') }}
            </router-link>
          </div>

          <div
            v-else-if="errorCodeRef === 'expired'"
            class="flex flex-col items-center gap-5 text-center"
            data-test-id="invitation-error-expired"
          >
            <p class="text-sm text-muted-foreground">{{ t('invitation.error.expired') }}</p>
            <router-link
              :to="ROUTES.INDEX"
              data-test-id="invitation-error-back-to-login"
              class="inline-flex items-center gap-1.5 text-sm font-medium !text-primary !no-underline hover:!underline"
            >
              <ArrowLeftIcon class="size-4" />
              {{ t('invitation.errorBackToLogin') }}
            </router-link>
          </div>

          <div
            v-else-if="errorCodeRef === 'used'"
            class="flex flex-col items-center gap-5 text-center"
            data-test-id="invitation-error-used"
          >
            <p class="text-sm text-muted-foreground">{{ t('invitation.error.used') }}</p>
            <router-link
              :to="ROUTES.INDEX"
              data-test-id="invitation-error-back-to-login"
              class="inline-flex items-center gap-1.5 text-sm font-medium !text-primary !no-underline hover:!underline"
            >
              <ArrowLeftIcon class="size-4" />
              {{ t('invitation.errorBackToLogin') }}
            </router-link>
          </div>

          <form
            v-else
            class="flex flex-col gap-6"
            @submit.prevent="onSubmit"
          >
            <div class="flex flex-col gap-2">
              <Label for="invitation-email">{{ t('invitation.email.label') }}</Label>
              <Input
                id="invitation-email"
                type="email"
                disabled
                :model-value="emailRef"
                data-test-id="invitation-email-display"
                class="bg-muted text-foreground disabled:opacity-100"
              />
            </div>

            <FormField
              v-slot="{ componentField }"
              name="password"
              :validate-on-model-update="false"
              :validate-on-blur="false"
            >
              <FormItem>
                <FormLabel>{{ t('invitation.password.label') }}</FormLabel>
                <InputGroup>
                  <FormControl>
                    <InputGroupInput
                      :type="showPasswordRef ? 'text' : 'password'"
                      data-test-id="invitation-password-input"
                      v-bind="componentField"
                    />
                  </FormControl>
                  <InputGroupAddon align="inline-end">
                    <button
                      type="button"
                      class="inline-flex size-6 shrink-0 items-center justify-center rounded-[calc(var(--radius)-5px)] text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&_svg]:size-3.5"
                      :aria-label="showPasswordRef ? t('ui.input.password.hide') : t('ui.input.password.show')"
                      :title="showPasswordRef ? t('ui.input.password.hide') : t('ui.input.password.show')"
                      @click="showPasswordRef = !showPasswordRef"
                    >
                      <EyeOffIcon v-if="showPasswordRef" aria-hidden="true" />
                      <EyeIcon v-else aria-hidden="true" />
                    </button>
                  </InputGroupAddon>
                </InputGroup>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField
              v-slot="{ componentField }"
              name="confirmPassword"
              :validate-on-model-update="false"
              :validate-on-blur="false"
            >
              <FormItem>
                <FormLabel>{{ t('invitation.confirmPassword.label') }}</FormLabel>
                <InputGroup>
                  <FormControl>
                    <InputGroupInput
                      :type="showConfirmPasswordRef ? 'text' : 'password'"
                      data-test-id="invitation-confirm-password-input"
                      v-bind="componentField"
                    />
                  </FormControl>
                  <InputGroupAddon align="inline-end">
                    <button
                      type="button"
                      class="inline-flex size-6 shrink-0 items-center justify-center rounded-[calc(var(--radius)-5px)] text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&_svg]:size-3.5"
                      :aria-label="showConfirmPasswordRef ? t('ui.input.password.hide') : t('ui.input.password.show')"
                      :title="showConfirmPasswordRef ? t('ui.input.password.hide') : t('ui.input.password.show')"
                      @click="showConfirmPasswordRef = !showConfirmPasswordRef"
                    >
                      <EyeOffIcon v-if="showConfirmPasswordRef" aria-hidden="true" />
                      <EyeIcon v-else aria-hidden="true" />
                    </button>
                  </InputGroupAddon>
                </InputGroup>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField v-slot="{ value, handleChange }" name="cguAccepted">
              <FormItem class="flex flex-col gap-2">
                <div class="flex items-start gap-3">
                  <Checkbox
                    id="invitation-cgu-checkbox"
                    :model-value="value"
                    data-test-id="invitation-cgu-checkbox"
                    class="mt-0.5"
                    @update:model-value="handleChange"
                  />
                  <Label for="invitation-cgu-checkbox" class="leading-snug">
                    <i18n-t keypath="invitation.cgu.label" tag="span">
                      <template #cgu>
                        <a
                          href="/cgu"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="!text-primary !no-underline"
                        >
                          {{ t('invitation.cgu.cguLink') }}
                        </a>
                      </template>
                      <template #privacy>
                        <a
                          href="/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="!text-primary !no-underline"
                        >
                          {{ t('invitation.cgu.privacyLink') }}
                        </a>
                      </template>
                    </i18n-t>
                  </Label>
                </div>
                <FormMessage />
              </FormItem>
            </FormField>

            <Button type="submit" data-test-id="invitation-submit-button" class="w-full">
              {{ t('invitation.submit') }}
            </Button>
          </form>
        </CardContent>
      </Card>
    </template>
  </AuthWrapper>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { toTypedSchema } from '@vee-validate/zod'
import { ArrowLeftIcon, EyeIcon, EyeOffIcon } from 'lucide-vue-next'
import { useForm } from 'vee-validate'
import { onMounted, ref, watch } from 'vue'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group'
import { Label } from '@/components/ui/label'
import { acceptInvitationMutation, getInvitation } from '@/features/auth/api'
import AuthWrapper from '@/features/auth/components/auth-wrapper/auth-wrapper.vue'
import useRouter from '@/hooks/use-router.hook'
import useToast from '@/hooks/use-toasts.hook'
import useWhiteLabel from '@/hooks/use-white-label.hook'
import { useI18n } from '@/lib/i18n'
import { ROUTES } from '@/utils/config/constants'
import { debounce } from '@/utils/math/debounce'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { whiteLabelRef } = useWhiteLabel()

const tokenRef = ref<string | null>(null)
const emailRef = ref<string>('')
const isLoadingRef = ref(true)
const errorCodeRef = ref<'invalid' | 'expired' | 'used' | null>(null)
const showPasswordRef = ref(false)
const showConfirmPasswordRef = ref(false)

const invitationSchema = toTypedSchema(
  z
    .object({
      password: z
        .string({ required_error: t('auth.password.errors.required') })
        .min(8, t('auth.password.errors.minLength'))
        .regex(/(?=.*[a-z])/, t('auth.password.errors.lowercase'))
        .regex(/(?=.*[A-Z])/, t('auth.password.errors.uppercase'))
        .regex(/(?=.*[0-9])/, t('auth.password.errors.number'))
        .regex(/(?=.*[@$!%*?&\-_#])/, t('auth.password.errors.specialChar')),
      confirmPassword: z.string({ required_error: t('auth.password.errors.required') }),
      cguAccepted: z.literal(true, {
        errorMap: () => ({ message: t('invitation.cgu.errors.required') }),
      }),
    })
    .refine((d) => d.password === d.confirmPassword, {
      path: ['confirmPassword'],
      message: t('invitation.error.passwordsDoNotMatch'),
    }),
)

const { handleSubmit, values, setFieldError, validateField } = useForm({
  validationSchema: invitationSchema,
  initialValues: { password: '', confirmPassword: '', cguAccepted: false },
})

const debouncedValidatePassword = debounce(() => {
  validateField('password')
}, 1000)

const debouncedValidateConfirmPassword = debounce(() => {
  const confirm = values.confirmPassword ?? ''
  if (!confirm) return
  if (values.password && confirm !== values.password) {
    setFieldError('confirmPassword', t('invitation.error.passwordsDoNotMatch'))
    return
  }
  validateField('confirmPassword')
}, 1000)

watch(
  () => values.password,
  () => {
    setFieldError('password', undefined)
    debouncedValidatePassword()
    if (values.confirmPassword) {
      setFieldError('confirmPassword', undefined)
      debouncedValidateConfirmPassword()
    }
  },
)

watch(
  () => values.confirmPassword,
  () => {
    setFieldError('confirmPassword', undefined)
    debouncedValidateConfirmPassword()
  },
)

onMounted(async () => {
  const token = router.currentRoute?.params?.token as string | undefined
  if (!token) {
    errorCodeRef.value = 'invalid'
    isLoadingRef.value = false
    return
  }
  tokenRef.value = token

  try {
    const result = await getInvitation(token)
    emailRef.value = result.email
  } catch (error: any) {
    const code = error?.response?.code as 'invalid' | 'expired' | 'used' | undefined
    errorCodeRef.value = code ?? 'invalid'
  } finally {
    isLoadingRef.value = false
  }
})

const { mutate: acceptInvitation } = useMutation({
  mutationFn: acceptInvitationMutation,
  onSuccess: () => {
    toast?.success(t('invitation.success'))
    router?.push(ROUTES.INDEX)
  },
  onError: (error: any) => {
    const code = error?.response?.code as 'invalid' | 'expired' | 'used' | undefined
    if (code) {
      errorCodeRef.value = code
      return
    }
    toast?.error(error?.message || t('invitation.error.generic'))
  },
})

const onSubmit = handleSubmit((submittedValues) => {
  if (!tokenRef.value) return
  acceptInvitation({
    token: tokenRef.value,
    password: submittedValues.password as string,
    confirmPassword: submittedValues.confirmPassword as string,
  })
})
</script>
